import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { cn } from '@/lib/utils'

const fieldStyles =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

/*
 * The id a <Field> minted for whatever control it wraps.
 *
 * Every form in this app is <Field label="Title"><Input /></Field>, and
 * `htmlFor` was optional and never once passed — 103 fields, none of them
 * associated. The label sat next to its box and belonged to nothing: clicking
 * it did not focus the field, and a screen reader read the control unnamed.
 * Checked with `el.labels`, the browser's own answer, not by inspection.
 *
 * Fixed here rather than at 103 call sites, so a form written tomorrow is
 * right without anyone remembering. A control that is given its own `id`
 * keeps it.
 */
export const FieldIdContext = React.createContext<string | undefined>(undefined)

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type = 'text', id, ...props }, ref) => {
    // Unconditionally, at the top. `id ?? useContext(...)` short-circuits, so
    // the hook would go uncalled whenever a control carries its own id — and
    // React counts hooks per render.
    const fieldId = React.useContext(FieldIdContext)
    return (
      <input
        ref={ref}
        type={type}
        id={id ?? fieldId}
        className={cn(fieldStyles, 'h-9', className)}
        {...props}
      />
    )
  },
)
Input.displayName = 'Input'

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, id, ...props }, ref) => {
  const fieldId = React.useContext(FieldIdContext)
  return (
    <textarea
      ref={ref}
      id={id ?? fieldId}
      className={cn(fieldStyles, 'min-h-[80px] resize-y', className)}
      {...props}
    />
  )
})
Textarea.displayName = 'Textarea'

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn('text-sm font-medium leading-none text-foreground', className)}
    {...props}
  />
))
Label.displayName = 'Label'

/** Label + control + optional hint, the shape used by every form in the app. */
export function Field({
  label,
  hint,
  htmlFor,
  group,
  className,
  children,
}: {
  label: string
  hint?: string
  htmlFor?: string
  /**
   * This field holds a SET of controls — a row of checkboxes, a custom
   * picker — rather than one. A `for` pointing at a single id would be a
   * lie, so the label is left unassociated here deliberately.
   */
  group?: boolean
  className?: string
  children: React.ReactNode
}) {
  const auto = React.useId()
  const id = htmlFor ?? auto
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={group ? undefined : id}>{label}</Label>
      {group ? children : <FieldIdContext.Provider value={id}>{children}</FieldIdContext.Provider>}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}
